import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOnlineStatus } from 'hooks/useOnlineStatus'
import { WifiOff, RefreshCw, CloudOff, CheckCircle, AlertTriangle, X } from 'lucide-react'
import {
 getSyncAttentionSnapshot,
 attentionTotalFromPendingActions,
 type PendingActionsSnapshot,
} from 'services/offlineBootstrap'
import { useOfflineBootstrapStatus } from 'hooks/useOfflineBootstrapStatus'

const SESSION_AUTH_BOOTSTRAP_BANNER_KEY = 'erp_offline_bootstrap_success_banner_auth'

const OfflineBanner: React.FC = () => {
 const { t } = useTranslation()
 const { isOnline, isOffline } = useOnlineStatus()
 const { status: bootstrapStatus, bootstrapRunning, syncing, syncNow, summary } = useOfflineBootstrapStatus()
 const [justCameBack, setJustCameBack] = useState(false)
 const [showBootstrapSuccess, setShowBootstrapSuccess] = useState(false)
 const [attentionSnap, setAttentionSnap] = useState<PendingActionsSnapshot>({
 pending_mutations: 0,
 failed_mutations: 0,
 pending_pos_orders: 0,
 failed_orders: 0,
 mutation_conflicts: 0,
 pos_order_conflicts: 0,
 pending_table_ops: 0,
 failed_table_ops: 0,
 table_op_conflicts: 0,
 })
 const [showBanner, setShowBanner] = useState(false)
 const wasOfflineRef = useRef(false)
 const previousBootstrapStatusRef = useRef<string | null>(null)

 useEffect(() => {
 const refreshCounts = () => {
 void getSyncAttentionSnapshot().then(setAttentionSnap).catch(() => {})
 }

 if (isOffline) {
 setShowBanner(true)
 setJustCameBack(false)
 wasOfflineRef.current = true
 refreshCounts()
 } else if (wasOfflineRef.current) {
 wasOfflineRef.current = false
 setJustCameBack(true)
 setShowBanner(true)
 refreshCounts()
 const timer = setTimeout(() => {
 setJustCameBack(false)
 setShowBanner(false)
 }, 3000)
 return () => clearTimeout(timer)
 } else {
 refreshCounts()
 }
 }, [isOnline, isOffline])

 useEffect(() => {
 if (isOffline || bootstrapRunning) {
 const interval = setInterval(() => {
 void getSyncAttentionSnapshot().then(setAttentionSnap).catch(() => {})
 }, 5000)
 return () => clearInterval(interval)
 }
 }, [isOffline, bootstrapRunning])

 useEffect(() => {
 const prev = previousBootstrapStatusRef.current
 const next = bootstrapStatus?.status ?? null
 const reason = bootstrapStatus?.reason ?? ''
 previousBootstrapStatusRef.current = next
 if (prev !== 'running' || next !== 'success') {
 return
 }
 if (reason === 'manual-sync') {
 return
 }
 if (reason === 'auth-restore') {
 try {
 if (sessionStorage.getItem(SESSION_AUTH_BOOTSTRAP_BANNER_KEY) === '1') {
 return
 }
 sessionStorage.setItem(SESSION_AUTH_BOOTSTRAP_BANNER_KEY, '1')
 } catch {
 /* ignore */
 }
 }
 setShowBootstrapSuccess(true)
 }, [bootstrapStatus?.status, bootstrapStatus?.reason])

 useEffect(() => {
 if (!showBootstrapSuccess) return
 const timer = window.setTimeout(() => setShowBootstrapSuccess(false), 3500)
 return () => window.clearTimeout(timer)
 }, [showBootstrapSuccess])

 const hasBootstrapFailure =
 isOnline && (bootstrapStatus?.status === 'partial_failed' || bootstrapStatus?.status === 'failed')

 const isVisible =
 showBanner || justCameBack || bootstrapRunning || hasBootstrapFailure || showBootstrapSuccess

 useEffect(() => {
 document.body.classList.toggle('has-app-banner', isVisible)
 return () => {
 document.body.classList.remove('has-app-banner')
 }
 }, [isVisible])

 if (!isVisible) return null

 if (justCameBack) {
 return (
 <div className="fixed top-0 left-0 right-0 z-[9999] h-10 bg-success text-success-foreground px-4 flex items-center justify-center gap-2 text-sm shadow-lg transition-all">
 <CheckCircle className="h-4 w-4"/>
 <span>{t('offline.connectionRestored')}</span>
 <RefreshCw className="h-4 w-4 animate-spin"/>
 </div>
 )
 }

 if (showBootstrapSuccess) {
 return (
 <div className="fixed top-0 left-0 right-0 z-[9999] h-10 bg-success text-success-foreground px-4 flex items-center justify-center gap-2 text-sm shadow-lg transition-all">
 <CheckCircle className="h-4 w-4 shrink-0"/>
 <span>{t('offline.readyForOffline')}</span>
 <button
 type="button" onClick={() => setShowBootstrapSuccess(false)}
 className="absolute end-3 top-1/2 -translate-y-1/2 rounded p-1 text-success-foreground/90 hover:bg-success-foreground/15 hover:text-success-foreground" aria-label={t('offline.close')}>
 <X className="h-4 w-4"/>
 </button>
 </div>
 )
 }

 if (bootstrapRunning && isOnline) {
 const progress =
 summary.totalModules > 0
 ? `${summary.completedModules}/${summary.totalModules}`
 : ''
 return (
 <div className="fixed top-0 left-0 right-0 z-[9999] h-10 bg-info text-info-foreground px-4 flex items-center justify-center gap-2 text-sm shadow-lg">
 <RefreshCw className="h-4 w-4 animate-spin"/>
 <span>{t('offline.preparingForOffline')}</span>
 {summary.runningModuleId && (
 <span className=" rounded-full border border-info-foreground/25 bg-info-foreground/15 px-2 py-0.5 text-xs font-semibold text-info-foreground">
 {summary.runningModuleId} {progress}
 </span>
 )}
 </div>
 )
 }

 if (hasBootstrapFailure) {
 const isFailed = bootstrapStatus?.status === 'failed'
 const failureHint = summary.failedModuleDetails
 .slice(0, 2)
 .map((detail) => `${detail.label}${detail.error ? ` · ${detail.error}` : ''}`)
 .join(' · ')
 return (
 <div className={`fixed top-0 left-0 right-0 z-[9999] min-h-10 ${isFailed ? 'bg-destructive text-destructive-foreground' : 'bg-warning text-warning-foreground'} px-4 py-2 flex flex-wrap items-center justify-center gap-2 text-sm shadow-lg`}>
 <AlertTriangle className="h-4 w-4 shrink-0"/>
 <span>{bootstrapStatus?.message || t('offline.partialBootstrap')}</span>
 {summary.failedModules > 0 && (
 <span className=" rounded-full border border-current/20 bg-background/15 px-2 py-0.5 text-xs font-semibold">{t('offline.failedCount', { count: summary.failedModules })}</span>
 )}
 {failureHint && (
 <span className="max-w-[min(100%,48rem)] truncate text-xs opacity-90">{failureHint}</span>
 )}
 <button
 type="button" onClick={() => { void syncNow() }}
 disabled={syncing}
 className=" rounded-full border border-current/20 bg-background/15 px-3 py-0.5 text-xs font-semibold hover:bg-background/25 disabled:">
 {syncing ? t('offline.syncing') : t('offline.retry')}
 </button>
 </div>
 )
 }

 const attentionTotal = attentionTotalFromPendingActions(attentionSnap)
 const pendingLabel =
 attentionSnap.pending_mutations +
 attentionSnap.pending_pos_orders +
 attentionSnap.pending_table_ops
 const conflictLabel =
 attentionSnap.mutation_conflicts +
 attentionSnap.pos_order_conflicts +
 attentionSnap.table_op_conflicts
 const failedLabel =
 attentionSnap.failed_orders +
 attentionSnap.failed_table_ops +
 attentionSnap.failed_mutations

 return (
 <div className="fixed top-0 left-0 right-0 z-[9999] h-10 bg-red-600 text-white px-4 flex items-center justify-center gap-2 text-sm shadow-lg">
 <WifiOff className="h-4 w-4"/>
 <span>{t('offline.noConnection')}</span>
 {attentionTotal > 0 && (
 <span className="bg-red-800 px-2 py-0.5 rounded-full text-xs font-bold flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 max-w-[min(100%,36rem)]">
 <CloudOff className="h-3 w-3 shrink-0"/>
 {pendingLabel > 0 && <span>{t('offline.pendingSync', { count: pendingLabel })}</span>}
 {pendingLabel > 0 && conflictLabel > 0 && <span className="mx-0.5">·</span>}
 {conflictLabel > 0 && <span>{t('offline.conflict', { count: conflictLabel })}</span>}
 {(pendingLabel > 0 || conflictLabel > 0) && failedLabel > 0 && (
 <span className="mx-0.5">·</span>
 )}
 {failedLabel > 0 && (
 <span className="flex items-center gap-0.5">
 <AlertTriangle className="h-3 w-3"/>
 {t('offline.failedOps', { count: failedLabel })}
 </span>
 )}
 </span>
 )}
 </div>
 )
}

export default OfflineBanner
